"use client";

/**
 * Verification Plan — yearly plan of which courses to verify
 * Path: /programs/{pid}/verificationPlans/{year}
 */
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc,
  serverTimestamp, Timestamp, writeBatch,
} from "firebase/firestore";
// Note: doc used for legacy course creation below
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFirebase } from "./client";

export interface PlannedCourse {
  courseId: string;        // normalized: "0537-113"
  courseCode: string;      // "0537 113"
  courseName: string;
  semester: string;        // "1/68" | "2/68"
  reason: string;
  scheduledDate?: string;  // ISO YYYY-MM-DD — set by chair when planning
  addedBy: string;         // "AI" | uid
  addedAt: Timestamp;
  status: "planned" | "in_progress" | "completed";
  verificationId?: string; // link to /verifications/{id} once started
}

export interface PlanCommitteeMember {
  uid: string;
  displayName: string;
  role: "chair" | "internal" | "external" | "secretary";
  external?: boolean;
  position?: string;
  affiliation?: string;
}

export interface VerificationPlan {
  year: number;
  status: "draft" | "active" | "completed";
  targetPercent: number;
  totalCourses: number;
  selectedCourses: PlannedCourse[];
  /** Committee assigned to ALL verifications in this year's plan */
  committee?: PlanCommitteeMember[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

// ---------- Hooks ----------

export function useVerificationPlan(programId: string, year: number) {
  return useQuery({
    queryKey: ["verification-plan", programId, year],
    queryFn: async (): Promise<VerificationPlan | null> => {
      const { db } = getFirebase();
      const snap = await getDoc(doc(db, "programs", programId, "verificationPlans", String(year)));
      if (!snap.exists()) return null;
      return snap.data() as VerificationPlan;
    },
  });
}

export function useSaveVerificationPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      year: number;
      plan: Partial<VerificationPlan>;
      creatorUid: string;
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "verificationPlans", String(args.year));
      const existing = await getDoc(ref);
      if (!existing.exists()) {
        await setDoc(ref, {
          ...args.plan,
          year: args.year,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: args.creatorUid,
          status: args.plan.status ?? "draft",
          targetPercent: args.plan.targetPercent ?? 25,
        });
      } else {
        await updateDoc(ref, {
          ...args.plan,
          updatedAt: serverTimestamp(),
        });
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["verification-plan", vars.programId, vars.year] });
    },
  });
}

/**
 * Mark courses as previously verified.
 * Used to import historical verifications (e.g., 2567 from PDF report).
 */
export function useImportHistoricalVerifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      year: number;
      courseCodes: string[];     // e.g., ["0537 112", "0540 201"]
    }) => {
      const { db } = getFirebase();
      const batch = writeBatch(db);

      const coursesSnap = await getDocs(collection(db, "programs", args.programId, "courses"));
      const allCourses: string[] = [];
      const matchedCourses: string[] = [];
      const skippedAlreadyVerified: string[] = [];
      let updated = 0;

      for (const courseDoc of coursesSnap.docs) {
        const courseData = courseDoc.data();
        const code = (courseData.code ?? "") as string;
        allCourses.push(code);

        const codeNoSpace = code.replace(/\s+/g, "");
        const matched = args.courseCodes.some(target =>
          target === code || target.replace(/\s+/g, "") === codeNoSpace
        );

        if (matched) {
          matchedCourses.push(code);
          const existingYears = (courseData.verifiedYears ?? []) as number[];
          if (!existingYears.includes(args.year)) {
            batch.update(courseDoc.ref, {
              verifiedYears: [...existingYears, args.year],
              lastVerifiedYear: Math.max(...existingYears, args.year),
              verificationCount: (courseData.verificationCount ?? 0) + 1,
            });
            updated++;
          } else {
            skippedAlreadyVerified.push(code);
          }
        }
      }

      const notFound = args.courseCodes.filter(target =>
        !matchedCourses.some(m => m === target || m.replace(/\s+/g, "") === target.replace(/\s+/g, ""))
      );

      // ============ NEW: Auto-create stub for legacy courses ============
      // Courses verified in past years that aren't in the new curriculum
      // (e.g., 0537 320 was in หลักสูตร 2562 but removed in 2567)
      const created: string[] = [];
      for (const code of notFound) {
        const courseId = code.replace(/\s+/g, "-");
        const ref = doc(db, "programs", args.programId, "courses", courseId);
        batch.set(ref, {
          programId: args.programId,
          code,
          nameTh: `(หลักสูตรเก่า — ทวนสอบ ${args.year})`,
          nameEn: "",
          credit: 3,
          creditString: "",
          category: "legacy",
          description: null,
          ploResponsibility: [],
          kscMapping: { k: [], s: [], e: [], c: [] },
          curriculumId: null,
          curriculumVersion: "legacy",
          instructorUid: "",
          instructorName: "",
          verificationCount: 1,
          verifiedYears: [args.year],
          lastVerifiedYear: args.year,
          active: true,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        created.push(code);
      }

      console.log("[ImportHistory] Diagnostic:", {
        totalCoursesInDB: allCourses.length,
        targetCodes: args.courseCodes,
        matchedCount: matchedCourses.length,
        matched: matchedCourses,
        notFound,
        createdAsLegacy: created,
        alreadyVerified: skippedAlreadyVerified,
        toUpdate: updated,
      });

      if (updated > 0 || created.length > 0) {
        await batch.commit();
      }

      return {
        updated,
        matched: matchedCourses,
        notFound,
        createdAsLegacy: created,
        alreadyVerified: skippedAlreadyVerified,
      };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["program-courses", vars.programId] });
    },
  });
}

/** List of years that have a saved plan (for the year-picker dropdown). */
export function useAvailablePlanYears(programId: string) {
  return useQuery({
    queryKey: ["plan-years", programId],
    queryFn: async (): Promise<number[]> => {
      const { db } = getFirebase();
      const snap = await getDocs(collection(db, "programs", programId, "verificationPlans"));
      const years = snap.docs.map(d => Number(d.id)).filter(n => !isNaN(n));
      return years.sort((a, b) => b - a); // newest first
    },
  });
}

// ====== Synthesis Report (Programme-Level) ======

export interface SynthesisReportDraft {
  executiveSummary?: {
    overallLevel?: string;
    systemicIssues?: string[];
    criticalIssues?: string[];
    strengths?: string[];
    keyMessages?: string[];
    threeLevelRecommendations?: { urgent: string; medium: string; long: string };
  };
  chapter3Findings?: Array<{ courseCode: string; instructor: string; findings: string[]; aunqaLevel: string }>;
  chapter4Systemic?: Array<{ issue: string; affectedCount: number; totalCount: number; detail: string; aunqaImpact: string }>;
  chapter5AunqaMapping?: Array<{ criterion: string; essence: string; programmeLevel: string; findings: string }>;
  chapter6ActionPlan?: {
    urgent?: Array<{ item: string; owner: string; timeframe: string; kpi: string }>;
    medium?: Array<{ item: string; owner: string; timeframe: string; kpi: string }>;
    long?: Array<{ item: string; owner: string; timeframe: string; kpi: string }>;
  };
  chapter7?: { conclusion: string; recommendations: string[]; closingMessage: string };
  kpis?: Array<{ name: string; target: string; evaluationTime: string }>;
  timeline?: Array<{ period: string; action: string; expected: string }>;
}

export interface SynthesisReport {
  year: number;
  draft?: SynthesisReportDraft;
  accepted?: SynthesisReportDraft;
  draftGeneratedAt?: unknown;
}

export function useSynthesisReport(programId: string, year: number) {
  return useQuery({
    queryKey: ["synthesis-report", programId, year],
    queryFn: async (): Promise<SynthesisReport | null> => {
      const { db } = getFirebase();
      const snap = await getDoc(doc(db, "programs", programId, "synthesisReports", String(year)));
      if (!snap.exists()) return null;
      return snap.data() as SynthesisReport;
    },
  });
}

export function useGenerateSynthesisReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; year: number }) => {
      const { app } = getFirebase();
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const functions = getFunctions(app, "asia-east1");
      const fn = httpsCallable(functions, "generateSynthesisReport");
      const res = await fn(args);
      return res.data as { ok: boolean; draft?: SynthesisReportDraft; tokensIn?: number; tokensOut?: number; costUSD?: number };
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["synthesis-report", vars.programId, vars.year] });
    },
  });
}

export function useSaveSynthesisReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { programId: string; year: number; accepted: SynthesisReportDraft }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "synthesisReports", String(args.year));
      await setDoc(ref, {
        year: args.year,
        accepted: args.accepted,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["synthesis-report", vars.programId, vars.year] });
    },
  });
}

/** Scan all yearly plans across a program and return planned-review items
 * where the user is in plan.committee. Used by the committee dashboard
 * to show upcoming reviews even before a /verifications/{id} doc exists. */
export interface MyPlannedReview {
  year: number;
  planStatus: string;
  courseId: string;
  courseCode: string;
  courseName: string;
  semester: string;
  reason: string;
  scheduledDate: string;       // ISO YYYY-MM-DD, may be empty
  verificationId?: string;
  status: "planned" | "in_progress" | "completed";
  roleInCommittee: "chair" | "internal" | "external" | "secretary";
}

/**
 * For an INSTRUCTOR: list courses they teach which are scheduled in any verification
 * plan (planned or started). Used by the "งานทวนสอบ" menu so instructors see only
 * verification-relevant work — not all the courses they teach.
 *
 * Matching is by courseCode (the plan stores courseCode strings; the instructor's
 * courses also have a code field). We compare normalized codes (whitespace-stripped).
 */
export function useMyVerificationCourses(programId: string, uid: string | null) {
  return useQuery({
    queryKey: ["my-verification-courses", programId, uid],
    enabled: !!programId && !!uid,
    queryFn: async (): Promise<Array<{
      year: number;
      planStatus: VerificationPlan["status"];
      courseId: string;
      courseCode: string;
      courseName: string;
      semester: string;
      reason: string;
      scheduledDate: string;
      verificationId?: string;
      verificationStatus: "planned" | "in_progress" | "completed";
    }>> => {
      if (!uid) return [];
      const { db } = getFirebase();
      // 1) Get all course IDs taught by this instructor (by instructorUid OR section instructorUid)
      const coursesSnap = await getDocs(collection(db, "programs", programId, "courses"));
      const myCourseCodes = new Set<string>();
      const myCourseIds = new Set<string>();
      for (const cDoc of coursesSnap.docs) {
        const d = cDoc.data() as { code?: string; instructorUid?: string; sections?: Array<{ instructorUid?: string }> };
        const mine = d.instructorUid === uid
          || (d.sections ?? []).some(s => s.instructorUid === uid);
        if (mine) {
          if (d.code) myCourseCodes.add(d.code.replace(/\s+/g, ""));
          myCourseIds.add(cDoc.id);
        }
      }
      if (myCourseCodes.size === 0 && myCourseIds.size === 0) return [];
      // 2) Scan all verification plans
      const plansSnap = await getDocs(collection(db, "programs", programId, "verificationPlans"));
      const items: Array<{
        year: number;
        planStatus: VerificationPlan["status"];
        courseId: string;
        courseCode: string;
        courseName: string;
        semester: string;
        reason: string;
        scheduledDate: string;
        verificationId?: string;
        verificationStatus: "planned" | "in_progress" | "completed";
      }> = [];
      for (const planDoc of plansSnap.docs) {
        const data = planDoc.data() as VerificationPlan;
        for (const c of data.selectedCourses ?? []) {
          const normCode = (c.courseCode ?? "").replace(/\s+/g, "");
          const match = myCourseCodes.has(normCode) || myCourseIds.has(c.courseId);
          if (!match) continue;
          items.push({
            year: data.year,
            planStatus: data.status,
            courseId: c.courseId,
            courseCode: c.courseCode,
            courseName: c.courseName,
            semester: c.semester,
            reason: c.reason,
            scheduledDate: c.scheduledDate ?? "",
            verificationId: c.verificationId,
            verificationStatus: c.status,
          });
        }
      }
      // Sort by scheduledDate asc, then year desc, then code
      items.sort((a, b) => {
        const da = a.scheduledDate || "9999-12-31";
        const db_ = b.scheduledDate || "9999-12-31";
        if (da !== db_) return da.localeCompare(db_);
        if (a.year !== b.year) return b.year - a.year;
        return a.courseCode.localeCompare(b.courseCode);
      });
      return items;
    },
  });
}

export function useMyPlannedReviews(programId: string, uid: string | null) {
  return useQuery({
    queryKey: ["my-planned-reviews", programId, uid],
    enabled: !!programId && !!uid,
    queryFn: async (): Promise<MyPlannedReview[]> => {
      if (!uid) return [];
      const { db } = getFirebase();
      const plansSnap = await getDocs(collection(db, "programs", programId, "verificationPlans"));
      const items: MyPlannedReview[] = [];
      for (const planDoc of plansSnap.docs) {
        const data = planDoc.data() as VerificationPlan;
        const me = (data.committee ?? []).find(m => m.uid === uid);
        if (!me) continue;
        for (const c of data.selectedCourses ?? []) {
          items.push({
            year: data.year,
            planStatus: data.status,
            courseId: c.courseId,
            courseCode: c.courseCode,
            courseName: c.courseName,
            semester: c.semester,
            reason: c.reason,
            scheduledDate: c.scheduledDate ?? "",
            verificationId: c.verificationId,
            status: c.status,
            roleInCommittee: me.role,
          });
        }
      }
      // Sort: scheduled date asc, then code
      items.sort((a, b) => {
        const da = a.scheduledDate || "9999-12-31";
        const db_ = b.scheduledDate || "9999-12-31";
        if (da !== db_) return da.localeCompare(db_);
        return a.courseCode.localeCompare(b.courseCode);
      });
      return items;
    },
  });
}

/** Save only the committee field of a plan (without affecting selectedCourses). */
export function useSavePlanCommittee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      programId: string;
      year: number;
      committee: PlanCommitteeMember[];
      creatorUid: string;
    }) => {
      const { db } = getFirebase();
      const ref = doc(db, "programs", args.programId, "verificationPlans", String(args.year));
      const existing = await getDoc(ref);
      // Sanitize — Firestore rejects undefined
      const cleanedCommittee = args.committee.map(m => ({
        uid: String(m.uid ?? ""),
        displayName: String(m.displayName ?? ""),
        role: m.role,
        external: m.external === true,
        position: String(m.position ?? ""),
        affiliation: String(m.affiliation ?? ""),
      }));
      if (!existing.exists()) {
        await setDoc(ref, {
          year: args.year,
          status: "draft",
          targetPercent: 25,
          totalCourses: 0,
          selectedCourses: [],
          committee: cleanedCommittee,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: args.creatorUid,
        });
      } else {
        await updateDoc(ref, {
          committee: cleanedCommittee,
          updatedAt: serverTimestamp(),
        });
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["verification-plan", vars.programId, vars.year] });
      qc.invalidateQueries({ queryKey: ["plan-years", vars.programId] });
    },
  });
}
